import { useState, useEffect } from "react";
import {
  Plus,
  Search,
  Edit,
  Trash2,
  CheckCircle,
  XCircle,
  Clock,
  Star,
  MoreVertical,
  AlertTriangle,
  Package,
  User,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  useGetAllTestimonialsQuery,
  useGetPendingTestimonialsQuery,
  useCreateTestimonialAdminMutation,
  useUpdateTestimonialMutation,
  useApproveTestimonialMutation,
  useRejectTestimonialMutation,
  useDeleteTestimonialMutation,
  type Testimonial,
  type CreateTestimonialData,
} from "@/store";
import { TablePagination } from "@/components/admin";
import { toast } from "sonner";
import type { TestimonialStatus } from "@/types/testimonial";

const DEFAULT_PAGE_SIZE = 20;

const STATUS_CONFIG: Record<
  TestimonialStatus,
  { label: string; color: string; icon: React.ReactNode }
> = {
  PENDING: {
    label: "Pending",
    color: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400",
    icon: <Clock className="h-3 w-3" />,
  },
  APPROVED: {
    label: "Approved",
    color: "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400",
    icon: <CheckCircle className="h-3 w-3" />,
  },
  REJECTED: {
    label: "Rejected",
    color: "bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400",
    icon: <XCircle className="h-3 w-3" />,
  },
};

export function AdminTestimonialsScreen() {
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(DEFAULT_PAGE_SIZE);
  const [statusFilter, setStatusFilter] = useState<"ALL" | TestimonialStatus>("ALL");
  const [typeFilter, setTypeFilter] = useState<"ALL" | "GENERAL" | "PRODUCT">("ALL");
  const [ratingFilter, setRatingFilter] = useState<"ALL" | number>("ALL");

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedTestimonial, setSelectedTestimonial] = useState<Testimonial | null>(null);

  // Form state
  const [formData, setFormData] = useState<CreateTestimonialData>({
    name: "",
    email: "",
    rating: 5,
    comment: "",
    avatarUrl: "",
    location: "",
    productId: undefined,
  });

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [statusFilter, typeFilter, ratingFilter]);

  const filters = {
    page,
    limit,
    status: statusFilter !== "ALL" ? statusFilter : undefined,
    isGeneral: typeFilter === "GENERAL" ? true : typeFilter === "PRODUCT" ? false : undefined,
    rating: ratingFilter !== "ALL" ? ratingFilter : undefined,
    search: debouncedSearch || undefined,
  };

  const { data: testimonialsData, isLoading } = useGetAllTestimonialsQuery(filters);
  const { data: pendingData } = useGetPendingTestimonialsQuery({
    page: 1,
    limit: 5,
  });

  const [createTestimonial, { isLoading: isCreating }] = useCreateTestimonialAdminMutation();
  const [updateTestimonial, { isLoading: isUpdating }] = useUpdateTestimonialMutation();
  const [approveTestimonial, { isLoading: isApproving }] = useApproveTestimonialMutation();
  const [rejectTestimonial, { isLoading: isRejecting }] = useRejectTestimonialMutation();
  const [deleteTestimonial, { isLoading: isDeleting }] = useDeleteTestimonialMutation();

  const testimonials = testimonialsData?.data || [];
  const pendingTestimonials = pendingData?.data || [];
  const pagination = testimonialsData?.pagination || {
    page: 1,
    limit: DEFAULT_PAGE_SIZE,
    total: 0,
    totalPages: 0,
  };

  const handleCreate = () => {
    setFormData({
      name: "",
      email: "",
      rating: 5,
      comment: "",
      avatarUrl: "",
      location: "",
      productId: undefined,
    });
    setSelectedTestimonial(null);
    setCreateDialogOpen(true);
  };

  const handleEdit = (testimonial: Testimonial) => {
    setFormData({
      name: testimonial.name || "",
      email: testimonial.email || "",
      rating: testimonial.rating,
      comment: testimonial.comment || "",
      avatarUrl: testimonial.avatarUrl || "",
      location: testimonial.location || "",
      productId: testimonial.productId || undefined,
    });
    setSelectedTestimonial(testimonial);
    setEditDialogOpen(true);
  };

  const handleDelete = (testimonial: Testimonial) => {
    setSelectedTestimonial(testimonial);
    setDeleteDialogOpen(true);
  };

  const handleSubmitCreate = async () => {
    try {
      if (!formData.name || !formData.rating || !formData.comment) {
        toast.error("Name, rating, and comment are required");
        return;
      }

      await createTestimonial({
        ...formData,
        status: "APPROVED", // Admin-created reviews are auto-approved
      }).unwrap();

      toast.success("Testimonial created successfully");
      setCreateDialogOpen(false);
      setFormData({
        name: "",
        email: "",
        rating: 5,
        comment: "",
        avatarUrl: "",
        location: "",
        productId: undefined,
      });
    } catch (error: any) {
      const errorMessage =
        error?.data?.error || error?.data?.message || "Failed to create testimonial";
      toast.error(errorMessage);
    }
  };

  const handleSubmitUpdate = async () => {
    if (!selectedTestimonial) return;

    try {
      await updateTestimonial({
        id: selectedTestimonial.id,
        data: formData,
      }).unwrap();

      toast.success("Testimonial updated successfully");
      setEditDialogOpen(false);
      setSelectedTestimonial(null);
    } catch (error: any) {
      const errorMessage =
        error?.data?.error || error?.data?.message || "Failed to update testimonial";
      toast.error(errorMessage);
    }
  };

  const handleApprove = async (id: string) => {
    try {
      await approveTestimonial(id).unwrap();
      toast.success("Testimonial approved successfully");
    } catch (error: any) {
      const errorMessage =
        error?.data?.error || error?.data?.message || "Failed to approve testimonial";
      toast.error(errorMessage);
    }
  };

  const handleReject = async (id: string) => {
    try {
      await rejectTestimonial(id).unwrap();
      toast.success("Testimonial rejected successfully");
    } catch (error: any) {
      const errorMessage =
        error?.data?.error || error?.data?.message || "Failed to reject testimonial";
      toast.error(errorMessage);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!selectedTestimonial) return;

    try {
      await deleteTestimonial(selectedTestimonial.id).unwrap();
      toast.success("Testimonial deleted successfully");
      setDeleteDialogOpen(false);
      setSelectedTestimonial(null);
    } catch (error: any) {
      const errorMessage =
        error?.data?.error || error?.data?.message || "Failed to delete testimonial";
      toast.error(errorMessage);
    }
  };

  const renderStars = (rating: number) => {
    return (
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`h-4 w-4 ${
              star <= rating
                ? "fill-yellow-400 text-yellow-400"
                : "fill-gray-200 text-gray-200 dark:fill-gray-700 dark:text-gray-700"
            }`}
          />
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Testimonials</h1>
          <p className="text-muted-foreground">Manage customer reviews and testimonials</p>
        </div>
        <Button onClick={handleCreate} className="gap-2">
          <Plus className="h-4 w-4" />
          Add Testimonial
        </Button>
      </div>

      {/* Pending Reviews Alert */}
      {pendingTestimonials.length > 0 && (
        <Card className="border-yellow-200 bg-yellow-50 dark:border-yellow-900 dark:bg-yellow-900/10">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-yellow-800 dark:text-yellow-400">
              <AlertTriangle className="h-5 w-5" />
              {pendingTestimonials.length} Review{pendingTestimonials.length !== 1 ? "s" : ""}{" "}
              Pending Approval
            </CardTitle>
            <CardDescription>
              Reviews flagged for moderation or awaiting admin approval
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {pendingTestimonials.slice(0, 3).map((testimonial) => (
                <div
                  key={testimonial.id}
                  className="flex items-center justify-between rounded-lg border bg-white p-3 dark:bg-gray-900"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">
                        {testimonial.name || testimonial.user?.name || "Anonymous"}
                      </span>
                      {renderStars(testimonial.rating)}
                      {testimonial.needsModeration && (
                        <Badge variant="destructive" className="text-xs">
                          Flagged
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground line-clamp-1">
                      {testimonial.comment}
                    </p>
                    {testimonial.moderationReason && (
                      <p className="text-xs text-red-600 dark:text-red-400">
                        Reason: {testimonial.moderationReason}
                      </p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleApprove(testimonial.id)}
                      disabled={isApproving}
                    >
                      <CheckCircle className="h-4 w-4 mr-1" />
                      Approve
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleReject(testimonial.id)}
                      disabled={isRejecting}
                    >
                      <XCircle className="h-4 w-4 mr-1" />
                      Reject
                    </Button>
                  </div>
                </div>
              ))}
              {pendingTestimonials.length > 3 && (
                <Button
                  variant="link"
                  className="w-full"
                  onClick={() => setStatusFilter("PENDING")}
                >
                  View all {pendingTestimonials.length} pending reviews
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Search and Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Search & Filter</CardTitle>
          <CardDescription>Find testimonials by name, comment, or filters</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search testimonials..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label>Status</Label>
                <Select
                  value={statusFilter}
                  onValueChange={(value) => setStatusFilter(value as "ALL" | TestimonialStatus)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ALL">All Statuses</SelectItem>
                    <SelectItem value="PENDING">Pending</SelectItem>
                    <SelectItem value="APPROVED">Approved</SelectItem>
                    <SelectItem value="REJECTED">Rejected</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Type</Label>
                <Select
                  value={typeFilter}
                  onValueChange={(value) =>
                    setTypeFilter(value as "ALL" | "GENERAL" | "PRODUCT")
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ALL">All Types</SelectItem>
                    <SelectItem value="GENERAL">General Testimonials</SelectItem>
                    <SelectItem value="PRODUCT">Product Reviews</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Rating</Label>
                <Select
                  value={ratingFilter === "ALL" ? "ALL" : String(ratingFilter)}
                  onValueChange={(value) =>
                    setRatingFilter(value === "ALL" ? "ALL" : parseInt(value))
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ALL">All Ratings</SelectItem>
                    <SelectItem value="5">5 Stars</SelectItem>
                    <SelectItem value="4">4 Stars</SelectItem>
                    <SelectItem value="3">3 Stars</SelectItem>
                    <SelectItem value="2">2 Stars</SelectItem>
                    <SelectItem value="1">1 Star</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Testimonials Table */}
      <Card>
        <CardHeader>
          <CardTitle>All Testimonials</CardTitle>
          <CardDescription>
            {pagination.total} {pagination.total === 1 ? "testimonial" : "testimonials"} found
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-center py-8 text-muted-foreground">Loading testimonials...</div>
          ) : testimonials.length === 0 ? (
            <div className="text-center py-12">
              <User className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground mb-4">No testimonials found</p>
              <Button onClick={handleCreate}>
                <Plus className="h-4 w-4 mr-2" />
                Add Your First Testimonial
              </Button>
            </div>
          ) : (
            <>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Author</TableHead>
                      <TableHead>Rating</TableHead>
                      <TableHead>Comment</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {testimonials.map((testimonial) => {
                      const statusConfig = STATUS_CONFIG[testimonial.status];
                      return (
                        <TableRow key={testimonial.id}>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              {testimonial.avatarUrl ? (
                                <img
                                  src={testimonial.avatarUrl}
                                  alt={testimonial.name || "User"}
                                  className="h-8 w-8 rounded-full"
                                />
                              ) : (
                                <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center">
                                  <User className="h-4 w-4" />
                                </div>
                              )}
                              <div>
                                <p className="font-medium">
                                  {testimonial.name || testimonial.user?.name || "Anonymous"}
                                </p>
                                {testimonial.email && (
                                  <p className="text-xs text-muted-foreground">
                                    {testimonial.email}
                                  </p>
                                )}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>{renderStars(testimonial.rating)}</TableCell>
                          <TableCell>
                            <p className="max-w-md truncate">{testimonial.comment || "—"}</p>
                          </TableCell>
                          <TableCell>
                            {testimonial.productId ? (
                              <Badge variant="outline" className="gap-1">
                                <Package className="h-3 w-3" />
                                Product Review
                              </Badge>
                            ) : (
                              <Badge variant="secondary" className="gap-1">
                                <User className="h-3 w-3" />
                                General
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell>
                            <Badge className={statusConfig.color} variant="outline">
                              {statusConfig.icon}
                              <span className="ml-1">{statusConfig.label}</span>
                            </Badge>
                            {testimonial.needsModeration && (
                              <Badge variant="destructive" className="ml-2 text-xs">
                                Flagged
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon">
                                  <MoreVertical className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => handleEdit(testimonial)}>
                                  <Edit className="h-4 w-4 mr-2" />
                                  Edit
                                </DropdownMenuItem>
                                {testimonial.status === "PENDING" && (
                                  <>
                                    <DropdownMenuItem
                                      onClick={() => handleApprove(testimonial.id)}
                                    >
                                      <CheckCircle className="h-4 w-4 mr-2" />
                                      Approve
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                      onClick={() => handleReject(testimonial.id)}
                                    >
                                      <XCircle className="h-4 w-4 mr-2" />
                                      Reject
                                    </DropdownMenuItem>
                                  </>
                                )}
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  onClick={() => handleDelete(testimonial)}
                                  className="text-destructive"
                                >
                                  <Trash2 className="h-4 w-4 mr-2" />
                                  Delete
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>

              <TablePagination
                pagination={pagination}
                onPageChange={setPage}
                onLimitChange={(newLimit) => {
                  setLimit(newLimit);
                  setPage(1);
                }}
              />
            </>
          )}
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Testimonial</DialogTitle>
            <DialogDescription>
              Create a testimonial on behalf of a customer
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Customer name"
              />
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="customer@example.com"
              />
            </div>
            <div>
              <Label htmlFor="rating">Rating *</Label>
              <Select
                value={String(formData.rating)}
                onValueChange={(value) =>
                  setFormData({ ...formData, rating: parseInt(value) })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="5">5 Stars</SelectItem>
                  <SelectItem value="4">4 Stars</SelectItem>
                  <SelectItem value="3">3 Stars</SelectItem>
                  <SelectItem value="2">2 Stars</SelectItem>
                  <SelectItem value="1">1 Star</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="comment">Comment *</Label>
              <Textarea
                id="comment"
                value={formData.comment}
                onChange={(e) => setFormData({ ...formData, comment: e.target.value })}
                placeholder="Customer testimonial..."
                rows={4}
              />
            </div>
            <div>
              <Label htmlFor="avatarUrl">Avatar URL</Label>
              <Input
                id="avatarUrl"
                value={formData.avatarUrl}
                onChange={(e) => setFormData({ ...formData, avatarUrl: e.target.value })}
                placeholder="https://example.com/avatar.jpg"
              />
            </div>
            <div>
              <Label htmlFor="location">Location</Label>
              <Input
                id="location"
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                placeholder="City, Country"
              />
            </div>
            <div>
              <Label htmlFor="productId">Product ID (optional - leave empty for general testimonial)</Label>
              <Input
                id="productId"
                value={formData.productId || ""}
                onChange={(e) =>
                  setFormData({ ...formData, productId: e.target.value || undefined })
                }
                placeholder="Product ID (optional)"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSubmitCreate} disabled={isCreating}>
              {isCreating ? "Creating..." : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Testimonial</DialogTitle>
            <DialogDescription>Update testimonial details</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="edit-name">Name *</Label>
              <Input
                id="edit-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Customer name"
              />
            </div>
            <div>
              <Label htmlFor="edit-email">Email</Label>
              <Input
                id="edit-email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="customer@example.com"
              />
            </div>
            <div>
              <Label htmlFor="edit-rating">Rating *</Label>
              <Select
                value={String(formData.rating)}
                onValueChange={(value) =>
                  setFormData({ ...formData, rating: parseInt(value) })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="5">5 Stars</SelectItem>
                  <SelectItem value="4">4 Stars</SelectItem>
                  <SelectItem value="3">3 Stars</SelectItem>
                  <SelectItem value="2">2 Stars</SelectItem>
                  <SelectItem value="1">1 Star</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="edit-comment">Comment *</Label>
              <Textarea
                id="edit-comment"
                value={formData.comment}
                onChange={(e) => setFormData({ ...formData, comment: e.target.value })}
                placeholder="Customer testimonial..."
                rows={4}
              />
            </div>
            <div>
              <Label htmlFor="edit-avatarUrl">Avatar URL</Label>
              <Input
                id="edit-avatarUrl"
                value={formData.avatarUrl}
                onChange={(e) => setFormData({ ...formData, avatarUrl: e.target.value })}
                placeholder="https://example.com/avatar.jpg"
              />
            </div>
            <div>
              <Label htmlFor="edit-location">Location</Label>
              <Input
                id="edit-location"
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                placeholder="City, Country"
              />
            </div>
            {selectedTestimonial && (
              <div>
                <Label htmlFor="edit-status">Status</Label>
                <Select
                  value={selectedTestimonial.status}
                  onValueChange={(value) => {
                    if (selectedTestimonial) {
                      updateTestimonial({
                        id: selectedTestimonial.id,
                        data: { status: value as TestimonialStatus },
                      });
                    }
                  }}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="PENDING">Pending</SelectItem>
                    <SelectItem value="APPROVED">Approved</SelectItem>
                    <SelectItem value="REJECTED">Rejected</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSubmitUpdate} disabled={isUpdating}>
              {isUpdating ? "Updating..." : "Update"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this testimonial. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => {
                setSelectedTestimonial(null);
                setDeleteDialogOpen(false);
              }}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
